import { EBEPaymentMethodsName } from '../contracts';

const ADYEN_APM_CHANNEL_PREFIX = 'adyen_apm';

/**
 * Payment method names that use BroadcastChannel for redirect result handling.
 * All other APMs use the legacy tab-close polling approach.
 */
export const ADYEN_APM_USE_BROADCAST_CHANNEL: string[] = [
  EBEPaymentMethodsName.wechatpay,
  EBEPaymentMethodsName.alipay_hk,
  EBEPaymentMethodsName.alipay,
];

/**
 * Returns true if the given payment method should use BroadcastChannel
 * for redirect result handling instead of tab-close polling.
 */
export function shouldUseBroadcastChannelForAPM(paymentMethodName?: string): boolean {
  if (!paymentMethodName) return false;
  return ADYEN_APM_USE_BROADCAST_CHANNEL.includes(paymentMethodName);
}

function getChannelName(orderId: string): string {
  return `${ADYEN_APM_CHANNEL_PREFIX}_${orderId}`;
}

/**
 * Posts the Adyen redirect result to the BroadcastChannel.
 * Call from the status page when it loads with redirectResult in the URL.
 */
function postRedirectResult(orderId: string, redirectResult: string): void {
  if (typeof BroadcastChannel === 'undefined') return;

  const channel = new BroadcastChannel(getChannelName(orderId));
  channel.postMessage({ redirectResult });
  channel.close();
}

export interface SubscribeOptions {
  timeoutMs?: number;
  onTimeout?: () => void;
}

/**
 * Subscribes to Adyen redirect result from the BroadcastChannel.
 * Call from the checkout when redirecting the user to the payment provider.
 * Returns a cleanup function to unsubscribe.
 */
function subscribe(
  orderId: string,
  onRedirectResult: (redirectResult: string) => void,
  options?: SubscribeOptions,
): any {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }

  const channel = new BroadcastChannel(getChannelName(orderId));
  let isHandled = false;

  const handleMessage = (event: MessageEvent) => {
    if (isHandled) return;
    const { redirectResult } = event.data || {};
    if (redirectResult) {
      isHandled = true;
      cleanup();
      onRedirectResult(redirectResult);
    }
  };

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (options?.timeoutMs) {
    timeoutId = setTimeout(() => {
      if (!isHandled) {
        isHandled = true;
        cleanup();
        options?.onTimeout?.();
      }
    }, options.timeoutMs);
  }

  const cleanup = () => {
    if (timeoutId) clearTimeout(timeoutId);
    channel.close();
  };

  channel.onmessage = handleMessage;

  return cleanup;
}

export const adyenAPMBroadcastChannel = {
  postRedirectResult,
  subscribe,
};
